import { Link } from 'react-router-dom';
import { useTheme } from '../contexts/ThemeContext';
import { useSiteContent } from '../contexts/SiteContentContext';
import { Heart, Mail, Phone, MapPin } from 'lucide-react';

export const Footer = () => {
  const { currentTheme } = useTheme();
  const { getSection } = useSiteContent();
  
  const footerContent = getSection('footer');
  const generalContent = getSection('general');

  const footerLinks = {
    explore: [
      { label: 'Media Library', href: '/media' },
      { label: 'Our Apps', href: '/apps' },
      { label: 'Coaching Sessions', href: '/coaching' },
    ],
    freedoms: [
      { label: 'Financial Freedom', href: '/#financial-freedom' },
      { label: 'Mental Freedom', href: '/#mental-freedom' },
      { label: 'Physical Freedom', href: '/#physical-freedom' },
    ],
    support: [
      { label: 'Contact Us', href: '/messages' },
      { label: 'FAQ', href: '/#faq' },
      { label: 'Privacy Policy', href: '/privacy-policy' },
      { label: 'Terms of Service', href: '/terms-of-service' },
    ],
  };

  return (
    <footer 
      className="section-spacing relative"
      style={{ backgroundColor: currentTheme.foreground }}
    >
      <div className="container mx-auto px-4 md:px-8 max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-12 mb-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <Link to="/" className="flex items-center gap-3 mb-6">
              <div 
                className="w-12 h-12 rounded-xl flex items-center justify-center"
                style={{ background: `linear-gradient(135deg, ${currentTheme.primary}, ${currentTheme.accent})` }}
              >
                <span className="text-white font-display font-bold text-2xl">
                  {generalContent.logo_text || 'N'}
                </span>
              </div>
              <span className="font-display text-2xl font-medium text-white">
                {generalContent.site_name || 'Nspire Living Healthy'}
              </span>
            </Link>
            <p className="text-white/60 font-body mb-6 leading-relaxed">
              {footerContent.tagline || 'Transform your life through holistic coaching. Achieve financial, mental, and physical freedom.'}
            </p>
            <div className="flex flex-col gap-3 text-white/60">
              <a href={`mailto:${footerContent.email || 'hello@nspireliving.com'}`} className="flex items-center gap-3 hover:text-white transition-colors">
                <Mail className="w-4 h-4" />
                {footerContent.email || 'hello@nspireliving.com'}
              </a>
              <a href={`tel:${(footerContent.phone || '+1 (234) 567-890').replace(/\s/g, '')}`} className="flex items-center gap-3 hover:text-white transition-colors">
                <Phone className="w-4 h-4" />
                {footerContent.phone || '+1 (234) 567-890'}
              </a>
              <span className="flex items-center gap-3">
                <MapPin className="w-4 h-4" />
                {footerContent.location || 'Anywhere, Everywhere'}
              </span>
            </div>
          </div>

          {/* Links */}
          <div>
            <h4 className="font-display text-lg font-medium text-white mb-6">Explore</h4>
            <ul className="space-y-3">
              {footerLinks.explore.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-white/60 hover:text-white transition-colors font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg font-medium text-white mb-6">Freedoms</h4>
            <ul className="space-y-3">
              {footerLinks.freedoms.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-white/60 hover:text-white transition-colors font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          <div>
            <h4 className="font-display text-lg font-medium text-white mb-6">Support</h4>
            <ul className="space-y-3">
              {footerLinks.support.map((link) => (
                <li key={link.href}>
                  <Link 
                    to={link.href}
                    className="text-white/60 hover:text-white transition-colors font-body"
                  >
                    {link.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        </div>

        {/* Bottom Bar */}
        <div 
          className="pt-8 border-t flex flex-col md:flex-row items-center justify-between gap-4"
          style={{ borderColor: 'rgba(255,255,255,0.1)' }}
        >
          <p className="text-white/40 font-body text-sm">
            &copy; {new Date().getFullYear()} {footerContent.copyright_text || 'Nspire Living Healthy. All rights reserved.'}
          </p>
          <p className="text-white/40 font-body text-sm flex items-center gap-1">
            {footerContent.made_with_text || 'Made with'} <Heart className="w-4 h-4 text-red-400" /> {!footerContent.made_with_text && 'for a healthier world'}
          </p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
